export type ChatDeliveryStatus = "queued" | "sending" | "retrying" | "failed" | "sent";

export interface PendingChatOutboxItem {
    clientMessageId: string;
    tempId: number;
    userId: string;
    conversationId: number;
    parentMessageId?: number;
    text: string;
    doc?: unknown;
    file?: File;
    attachmentUrl?: string;
    attachmentType?: string;
    attachmentOriginalName?: string;
    attachmentContentType?: string;
    attachmentSize?: number;
    attachmentScanStatus?: string;
    createdAt: number;
    attemptCount: number;
    status: ChatDeliveryStatus;
    lastError?: string;
}

const DATABASE_NAME = "sprout-chat-outbox";
const STORE_NAME = "pending-messages";
const DATABASE_VERSION = 1;
const memoryFallback = new Map<string, PendingChatOutboxItem>();
let databasePromise: Promise<IDBDatabase> | null = null;

const canUseIndexedDb = (): boolean => typeof indexedDB !== "undefined";

const openDatabase = (): Promise<IDBDatabase> => {
    if (databasePromise) return databasePromise;
    databasePromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
        request.onerror = () => {
            databasePromise = null;
            reject(request.error);
        };
        request.onupgradeneeded = () => {
            const database = request.result;
            if (database.objectStoreNames.contains(STORE_NAME)) return;
            const store = database.createObjectStore(STORE_NAME, { keyPath: "clientMessageId" });
            store.createIndex("userConversation", ["userId", "conversationId"], { unique: false });
        };
        request.onsuccess = () => {
            const database = request.result;
            database.onversionchange = () => {
                database.close();
                databasePromise = null;
            };
            resolve(database);
        };
    });
    return databasePromise;
};

const runRequest = async <T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
    const database = await openDatabase();
    return new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, mode);
        const request = operation(transaction.objectStore(STORE_NAME));
        let result!: T;
        request.onsuccess = () => { result = request.result; };
        request.onerror = () => reject(request.error);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error ?? new Error("Chat outbox transaction was aborted."));
        transaction.oncomplete = () => resolve(result);
    });
};

export const createClientMessageId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
        const random = Math.floor(Math.random() * 16);
        const value = character === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

export const createOptimisticTempId = (clientMessageId: string): number =>
    -Math.max(1, Number.parseInt(clientMessageId.replace(/-/g, "").slice(0, 12), 16));

export const saveOutboxItem = async (item: PendingChatOutboxItem): Promise<void> => {
    memoryFallback.set(item.clientMessageId, item);
    if (!canUseIndexedDb()) return;
    await runRequest("readwrite", store => store.put(item));
};

export const updateOutboxItem = async (
    clientMessageId: string,
    patch: Partial<PendingChatOutboxItem>,
): Promise<PendingChatOutboxItem | null> => {
    const existing = memoryFallback.get(clientMessageId)
        ?? (canUseIndexedDb() ? await runRequest<PendingChatOutboxItem | undefined>("readonly", store => store.get(clientMessageId)) : undefined);
    if (!existing) return null;
    const updated = { ...existing, ...patch };
    await saveOutboxItem(updated);
    return updated;
};

export const deleteOutboxItem = async (clientMessageId: string): Promise<void> => {
    memoryFallback.delete(clientMessageId);
    if (!canUseIndexedDb()) return;
    await runRequest("readwrite", store => store.delete(clientMessageId));
};

export const getConversationOutbox = async (
    userId: string,
    conversationId: number,
): Promise<PendingChatOutboxItem[]> => {
    if (!canUseIndexedDb()) {
        return Array.from(memoryFallback.values())
            .filter(item => item.userId === userId && item.conversationId === conversationId)
            .sort((left, right) => left.createdAt - right.createdAt);
    }

    const items = await runRequest<PendingChatOutboxItem[]>("readonly", store =>
        store.index("userConversation").getAll([userId, conversationId]));
    items.forEach(item => memoryFallback.set(item.clientMessageId, item));
    return items.sort((left, right) => left.createdAt - right.createdAt);
};
